(function () {
  "use strict";

  const SELECTORS = {
    section: "[data-product-upsell]",
    slider: "[data-upsell-slider]",
    dots: "[data-upsell-dots]",
    prevButton: "[data-upsell-prev]",
    nextButton: "[data-upsell-next]",
    card: "[data-upsell-card]",
    image: "[data-upsell-image]",
    variantSelect: "[data-upsell-variant-select]",
    price: "[data-upsell-price]",
    addToCart: "[data-upsell-add-to-cart]",
    heading: "[data-upsell-heading]",
  };

  const DOT_CLASSES = {
    active: "size-6 flex items-center justify-center before:size-2 before:rounded-full before:transition-colors before:bg-primary",
    inactive: "size-6 flex items-center justify-center before:size-2 before:rounded-full before:transition-colors before:bg-disabled",
  };

  /**
   * Wait for EmblaCarousel to be available on window
   * @returns {Promise<void>}
   */
  const EMBLA_MAX_ATTEMPTS = 100;

  const waitForEmbla = () =>
    new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        if (typeof EmblaCarousel !== "undefined") {
          resolve();
        } else if (attempts >= EMBLA_MAX_ATTEMPTS) {
          reject(new Error("EmblaCarousel failed to load"));
        } else {
          attempts += 1;
          setTimeout(check, 50);
        }
      };
      check();
    });

  /**
   * Create a dot navigation button
   * @param {number} index - Dot index
   * @param {boolean} isActive - Whether this dot is active
   * @returns {HTMLButtonElement}
   */
  const createDot = (index, isActive) => {
    const dot = document.createElement("button");
    dot.className = isActive ? DOT_CLASSES.active : DOT_CLASSES.inactive;
    dot.setAttribute("role", "tab");
    dot.setAttribute("aria-selected", String(isActive));
    dot.setAttribute("aria-label", `Slide ${index + 1}`);
    dot.dataset.dotIndex = index;
    return dot;
  };

  /**
   * Update dot states based on selected index
   * @param {HTMLElement} dotsContainer - Container for dot buttons
   * @param {number} selectedIndex - Currently selected slide index
   */
  const updateDots = (dotsContainer, selectedIndex) => {
    dotsContainer.querySelectorAll("button").forEach((el, i) => {
      const isActive = i === selectedIndex;
      el.className = isActive ? DOT_CLASSES.active : DOT_CLASSES.inactive;
      el.setAttribute("aria-selected", String(isActive));
    });
  };

  /**
   * Render dot navigation buttons
   * @param {Object} embla - Embla carousel instance
   * @param {HTMLElement} dotsContainer - Container for dot buttons
   */
  const renderDots = (embla, dotsContainer) => {
    const scrollSnaps = embla.scrollSnapList();
    dotsContainer.replaceChildren();

    scrollSnaps.forEach((_, index) => {
      const dot = createDot(index, index === embla.selectedScrollSnap());
      dot.addEventListener("click", () => embla.scrollTo(index));
      dotsContainer.appendChild(dot);
    });
  };

  /**
   * Initialize variant selector for a product card
   * @param {HTMLElement} card - Product upsell card element
   */
  const initVariantSelector = (card) => {
    const select = card.querySelector(SELECTORS.variantSelect);
    const priceEl = card.querySelector(SELECTORS.price);
    const addBtns = card.querySelectorAll(SELECTORS.addToCart);
    const imageEl = card.querySelector(SELECTORS.image);

    if (!select || !priceEl || !addBtns.length) return;

    select.addEventListener("change", () => {
      const option = select.options[select.selectedIndex];
      priceEl.textContent = option.dataset.price;

      addBtns.forEach((element) => {
        element.dataset.variantId = option.value;
        element.disabled = option.disabled;
      });

      const { imageUrl } = option.dataset;
      if (imageUrl && imageEl) {
        imageEl.src = imageUrl;
        imageEl.srcset = "";
      }
    });
  };

  /**
   * Handle add-to-cart button click
   * @param {HTMLButtonElement} el - Add to cart button
   */
  const initAddToCart = (el) => {
    el.addEventListener("click", () => {
      const variantId = parseInt(el.dataset.variantId, 10);
      if (!variantId || el.disabled) return;

      el.disabled = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Add to cart failed");
          return response.json();
        })
        .then(() => {
          el.classList.add("!bg-success");
          setTimeout(() => {
            el.classList.remove("!bg-success");
            el.disabled = false;
          }, 1500);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          document.dispatchEvent(new CustomEvent("cart:open"));
        })
        .catch((error) => {
          console.warn("Product upsell: add to cart failed", error);
          el.disabled = false;
        });
    });
  };

  /**
   * Initialize Embla carousel and card interactivity
   * @param {HTMLElement} section - The section container
   */
  /**
   * Update navigation button disabled states based on carousel position
   * @param {Object} embla - Embla carousel instance
   * @param {HTMLElement} prevButton - Previous button element
   * @param {HTMLElement} nextButton - Next button element
   */
  const updateButtonStates = (embla, prevButton, nextButton) => {
    if (!prevButton || !nextButton) return;

    prevButton.disabled = !embla.canScrollPrev();
    nextButton.disabled = !embla.canScrollNext();
  };

  const initializeSection = async (section) => {
    const sliderNode = section.querySelector(SELECTORS.slider);
    const dotsContainer = section.querySelector(SELECTORS.dots);
    const prevButton = section.querySelector(SELECTORS.prevButton);
    const nextButton = section.querySelector(SELECTORS.nextButton);

    if (!sliderNode) return;

    try {
      await waitForEmbla();
    } catch (error) {
      console.warn("Product upsell:", error.message);
      return;
    }

    const embla = EmblaCarousel(sliderNode, {
      loop: false,
      align: "start",
      containScroll: "trimSnaps",
    });

    if (dotsContainer) {
      renderDots(embla, dotsContainer);

      embla.on("select", () =>
        updateDots(dotsContainer, embla.selectedScrollSnap()),
      );

      embla.on("reInit", () => renderDots(embla, dotsContainer));
    }

    if (prevButton) {
      prevButton.addEventListener("click", () => embla.scrollPrev());
    }
    if (nextButton) {
      nextButton.addEventListener("click", () => embla.scrollNext());
    }

    const onNavUpdate = () => updateButtonStates(embla, prevButton, nextButton);
    embla.on("select", onNavUpdate);
    embla.on("reInit", onNavUpdate);
    onNavUpdate();

    section.querySelectorAll(SELECTORS.card).forEach(initVariantSelector);
    section.querySelectorAll(SELECTORS.addToCart).forEach(initAddToCart);
  };

  /**
   * Fetch complementary products and replace section content
   * @param {HTMLElement} section - The section container with data-url attribute
   */
  const loadComplementaryProducts = (section) => {
    const { url } = section.dataset;
    if (!url) return;

    fetch(url)
      .then((response) => {
        if (!response.ok) return;
        return response.text();
      })
      .then((text) => {
        if (!text) return;

        const html = document.createElement("div");
        html.innerHTML = text;

        const recommendations = html.querySelector(SELECTORS.section);
        if (recommendations && recommendations.innerHTML.trim().length) {
          section.replaceChildren(...recommendations.childNodes);

          const { heading } = section.dataset;
          if (heading) {
            const headingEl = section.querySelector(SELECTORS.heading);
            if (headingEl) {
              headingEl.textContent = heading;
            }
          }

          initializeSection(section);
        }
      })
      .catch((error) => {
        console.warn("Product upsell: failed to load recommendations", error);
      });
  };

  /**
   * Initialize all product upsell sections using IntersectionObserver
   */
  const init = () => {
    const sections = document.querySelectorAll(SELECTORS.section);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          loadComplementaryProducts(entry.target);
        });
      },
      { rootMargin: "200px" },
    );

    sections.forEach((section) => observer.observe(section));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
 